// Frustration-signal detection. Runs alongside the normal click/move/scroll
// capture and emits separate event records the aggregator can count.
//
// Two detectors:
//   - rage clicks: 3+ clicks on the same element within RAGE_WINDOW_MS
//   - dead clicks: a click that produces no observable change (no DOM
//     mutation, no navigation, no focus change) within DEAD_WAIT_MS
//
// Both are wired by registerSignalDetectors(). The host calls it once with
// a callback that pushes the resulting events into the tracker's batch.

import type {
  RageClickEvent,
  DeadClickEvent,
} from "@hotmaps/shared/events";

export type SignalEmit = (ev: RageClickEvent | DeadClickEvent) => void;

const RAGE_WINDOW_MS = 1000;       // burst window
const RAGE_THRESHOLD = 3;          // clicks within window to count as rage
const RAGE_RADIUS_PX = 24;         // clicks within this radius count as "same target"

const DEAD_WAIT_MS = 1500;         // how long to wait before declaring a click dead
const DEAD_IGNORE_TAGS = new Set(["html", "body", "main", "section"]);
// Form controls are excluded from dead-click detection: a click on an
// already-focused input/select fires no focusin and mutates no DOM (caret
// moves, native dropdown opens) — flagging it as "dead" is a false positive
// that pollutes checkout conclusions (seen on comjet: label>input.cj-co__input,
// select.cj-coll__sort-select). Form trouble has its own signal —
// field_abandon — which measures what actually matters.
const DEAD_FORM_TAGS = new Set(["input", "select", "textarea", "label", "option"]);

// Lightweight helpers — duplicated from tracker.ts intentionally so signals
// stay self-contained and can be tree-shaken when we ship a "lite" build.
function relTsFactory(start: number) {
  return () => Date.now() - start;
}

function shortSelector(el: Element): string {
  const id = (el as HTMLElement).id;
  if (id) return `#${id}`.slice(0, 60);
  const tag = el.tagName.toLowerCase();
  const cls = (el.getAttribute("class") || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .join(".");
  const parent = el.parentElement;
  const parentTag = parent ? parent.tagName.toLowerCase() : "";
  const base = cls ? `${tag}.${cls}` : tag;
  return parentTag ? `${parentTag}>${base}` : base;
}

function truncatedText(el: Element): string | undefined {
  const t = (el as HTMLElement).innerText;
  if (!t) return undefined;
  return t.trim().replace(/\s+/g, " ").slice(0, 40) || undefined;
}

export function registerSignalDetectors(sessionStart: number, emit: SignalEmit) {
  const relTs = relTsFactory(sessionStart);

  // --- rage detector ----------------------------------------------------
  // Burst state — held until either threshold reached (emit) or window
  // expires (reset).
  type Burst = {
    startTs: number;
    lastTs: number;
    count: number;
    sx: number; // sum of x coords
    sy: number; // sum of y
    tag?: string;
    sel?: string;
    txt?: string;
    target: Element | null;
  };
  let burst: Burst | null = null;
  let burstResetTimer: number | undefined;

  function resetBurst() {
    burst = null;
    if (burstResetTimer !== undefined) {
      clearTimeout(burstResetTimer);
      burstResetTimer = undefined;
    }
  }
  function flushRage() {
    if (!burst || burst.count < RAGE_THRESHOLD) {
      resetBurst();
      return;
    }
    emit({
      t: "rage",
      ts: burst.startTs,
      endTs: burst.lastTs,
      count: burst.count,
      x: Math.round(burst.sx / burst.count),
      y: Math.round(burst.sy / burst.count),
      tag: burst.tag,
      sel: burst.sel,
      txt: burst.txt,
    });
    resetBurst();
  }

  // --- dead-click detector ---------------------------------------------
  // For each click we don't already know was "live", set up a one-shot
  // observer + timeout. If DEAD_WAIT_MS passes without a DOM mutation,
  // navigation, or focus change, we emit "dead".
  function watchForDead(target: Element, ts: number, px: number, py: number) {
    const tag = target.tagName.toLowerCase();
    if (DEAD_IGNORE_TAGS.has(tag)) return; // ignore clicks on the page itself
    if (DEAD_FORM_TAGS.has(tag)) return;   // form controls — see DEAD_FORM_TAGS

    let resolved = false;
    function resolve(live: boolean) {
      if (resolved) return;
      resolved = true;
      observer.disconnect();
      window.removeEventListener("hashchange", onChange);
      window.removeEventListener("popstate", onChange);
      window.removeEventListener("beforeunload", onChange);
      document.removeEventListener("focusin", onChange);
      if (!live) {
        emit({
          t: "dead",
          ts,
          x: px,
          y: py,
          tag,
          sel: shortSelector(target),
          txt: truncatedText(target),
        });
      }
    }
    const onChange = () => resolve(true);

    const observer = new MutationObserver(() => resolve(true));
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });
    window.addEventListener("hashchange", onChange);
    window.addEventListener("popstate", onChange);
    window.addEventListener("beforeunload", onChange);
    document.addEventListener("focusin", onChange);

    setTimeout(() => resolve(false), DEAD_WAIT_MS);
  }

  // --- single click listener that drives both detectors ----------------
  document.addEventListener(
    "click",
    (e) => {
      const target = e.target as Element | null;
      if (!target) return;
      const ts = relTs();
      const px = e.clientX + window.scrollX;
      const py = e.clientY + window.scrollY;

      // rage burst tracking ---------------------------------------------
      const sameTarget =
        burst &&
        ((burst.target && burst.target === target) ||
          Math.hypot((burst.sx / burst.count) - px, (burst.sy / burst.count) - py) <
            RAGE_RADIUS_PX);

      if (burst && sameTarget && ts - burst.lastTs < RAGE_WINDOW_MS) {
        burst.lastTs = ts;
        burst.count += 1;
        burst.sx += px;
        burst.sy += py;
      } else {
        // Burst broken: flush if it qualified, then start a fresh one.
        if (burst) flushRage();
        burst = {
          startTs: ts,
          lastTs: ts,
          count: 1,
          sx: px,
          sy: py,
          tag: target.tagName.toLowerCase(),
          sel: shortSelector(target),
          txt: truncatedText(target),
          target,
        };
      }
      // Schedule a flush if the window expires with no more clicks.
      if (burstResetTimer !== undefined) clearTimeout(burstResetTimer);
      burstResetTimer = window.setTimeout(flushRage, RAGE_WINDOW_MS);

      // dead-click watcher ----------------------------------------------
      watchForDead(target, ts, px, py);
    },
    { capture: true, passive: true }
  );

}
